import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface TabelaPrecoItem {
  id: number;
  descricao: string;
  valor: number | null;
  valorDollar: number | null;
  valorEuro: number | null;
  dataCadastro: string;
  inicioVigencia: string;
  fimVigencia: string;
  idFinDocumento: number;
  finDocumento: string;
}

interface Opcao {
  id: number;
  descricao: string;
}

@Component({
  selector: 'app-consultar-tabela-preco-fin',
  templateUrl: './consultar-tabela-preco-fin.component.html',
  styleUrls: ['./consultar-tabela-preco-fin.component.scss']
})
export class ConsultarTabelaPrecoFinComponent implements OnInit {
  itens: TabelaPrecoItem[] = [];
  itensPaginados: TabelaPrecoItem[] = [];
  documentos: Opcao[] = [];

  filtroDescricao = '';
  filtroValor: number | null = null;
  filtroValorDollar: number | null = null;
  filtroValorEuro: number | null = null;
  filtroInicioVigencia = '';
  filtroFimVigencia = '';
  filtroIdFinDocumento = 0;

  loading = false;
  paginaAtual = 1;
  itensPorPagina = 10;

  constructor(
    private ajaxService: AjaxService,
    private util: UtilService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.carregarOpcoes();
    this.buscar();
  }

  get totalPaginas(): number {
    return Math.ceil(this.itens.length / this.itensPorPagina) || 1;
  }

  carregarOpcoes(): void {
    this.ajaxService.get(`${API}financeiro/FinTabelaPreco/ConsultarOpcoes`).subscribe({
      next: (response) => {
        const data = this.desempacotarResponse(response);
        this.documentos = this.normalizarLista(data.documentos).map((x: any) => ({
          id: Number(x.id || 0),
          descricao: String(x.descricao || '')
        })).filter((x: Opcao) => x.id > 0);
      }
    });
  }

  novo(): void {
    this.router.navigate(['/cadastrar-tabela-preco-fin']);
  }

  buscar(): void {
    const params = new URLSearchParams();
    if (this.filtroDescricao.trim()) params.set('descricao', this.filtroDescricao.trim());
    if (this.filtroValor != null) params.set('valor', String(this.filtroValor));
    if (this.filtroValorDollar != null) params.set('valorDollar', String(this.filtroValorDollar));
    if (this.filtroValorEuro != null) params.set('valorEuro', String(this.filtroValorEuro));
    if (this.filtroInicioVigencia) params.set('inicioVigencia', this.filtroInicioVigencia);
    if (this.filtroFimVigencia) params.set('fimVigencia', this.filtroFimVigencia);
    if (this.filtroIdFinDocumento > 0) params.set('idFinDocumento', String(this.filtroIdFinDocumento));

    const query = params.toString();
    const url = `${API}financeiro/FinTabelaPreco/ConsultarFinTabelaPreco${query ? `?${query}` : ''}`;

    this.loading = true;
    this.util.showLoading();
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.loading = false;
        this.util.closeLoading();
        const lista = this.extrairListaResponse(response);
        this.itens = lista.map((x: any) => {
          const item = this.util.converterParaLowercase(x || {});
          return {
            id: Number(item.id || 0),
            descricao: String(item.descricao || ''),
            valor: item.valor != null ? Number(item.valor) : null,
            valorDollar: item.valordollar != null ? Number(item.valordollar) : null,
            valorEuro: item.valoreuro != null ? Number(item.valoreuro) : null,
            dataCadastro: this.formatarData(item.datacadastro),
            inicioVigencia: this.formatarData(item.iniciovigencia),
            fimVigencia: this.formatarData(item.fimvigencia),
            idFinDocumento: Number(item.idfindocumento || 0),
            finDocumento: String(item.findocumento || '')
          } as TabelaPrecoItem;
        }).filter((x: TabelaPrecoItem) => x.id > 0);
        this.paginaAtual = 1;
        this.atualizarPaginacao();
      },
      error: () => {
        this.loading = false;
        this.util.closeLoading();
        this.itens = [];
        this.itensPaginados = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar tabelas de preço.');
      }
    });
  }

  editar(item: TabelaPrecoItem): void {
    this.router.navigate(['/cadastrar-tabela-preco-fin'], { queryParams: { id: item.id } });
  }

  deletar(item: TabelaPrecoItem): void {
    this.util.showLoading();
    this.ajaxService.post(`${API}financeiro/FinTabelaPreco/Deletar`, { id: item.id }).subscribe({
      next: (response) => {
        this.util.closeLoading();
        if (response?.hasErrors) {
          this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir.');
          return;
        }
        this.util.toasterShowSuccess('Sucesso', 'Registro excluído com sucesso!');
        this.buscar();
      },
      error: () => {
        this.util.closeLoading();
        this.util.toasterShowError('Erro', 'Erro ao excluir tabela de preço.');
      }
    });
  }

  limparFiltros(): void {
    this.filtroDescricao = '';
    this.filtroValor = null;
    this.filtroValorDollar = null;
    this.filtroValorEuro = null;
    this.filtroInicioVigencia = '';
    this.filtroFimVigencia = '';
    this.filtroIdFinDocumento = 0;
    this.buscar();
  }

  paginaAnterior(): void {
    if (this.paginaAtual > 1) {
      this.paginaAtual--;
      this.atualizarPaginacao();
    }
  }

  proximaPagina(): void {
    if (this.paginaAtual < this.totalPaginas) {
      this.paginaAtual++;
      this.atualizarPaginacao();
    }
  }

  alterarItensPorPagina(): void {
    this.paginaAtual = 1;
    this.atualizarPaginacao();
  }

  private atualizarPaginacao(): void {
    const inicio = (this.paginaAtual - 1) * this.itensPorPagina;
    const fim = inicio + this.itensPorPagina;
    this.itensPaginados = this.itens.slice(inicio, fim);
  }

  private normalizarLista(data: any): any[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (typeof data === 'object') {
      if (Object.prototype.hasOwnProperty.call(data, 'id') || Object.prototype.hasOwnProperty.call(data, 'descricao')) return [data];
      return Object.values(data).filter((x: any) => x && typeof x === 'object');
    }
    return [];
  }

  private formatarData(value: any): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('pt-BR');
  }

  private desempacotarResponse(response: any): any {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.util.converterParaLowercase(atual ?? {});
  }

  private extrairListaResponse(response: any): any[] {
    let atual = response;
    for (let i = 0; i < 3; i++) {
      if (Array.isArray(atual)) break;
      if (atual && typeof atual === 'object' && Object.prototype.hasOwnProperty.call(atual, 'data') && atual.data != null) {
        atual = atual.data;
      } else {
        break;
      }
    }
    return this.normalizarLista(atual);
  }
}
